// src/components/map/LiveTracking.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Badge } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import { TRUCK_STATUSES } from '../../data/store';
import TripView from '../clients/TripView';
import { matchToRoad, getRoute } from '../../services/geocoding';
import {
  TraccarConfig, loadCredentialsFromBackend, fetchPositions, fetchTraccarDevices,
  parsePosition, getStatusFromPosition, reverseGeocode,
} from '../../services/traccar';

const POLL_INTERVAL = 5000; // 5 seconds — faster polling means more frequent position updates, so the truck icon's road-following animation reads as continuous movement instead of long pauses between hops
const CONNECTIVITY_POLL_MS = 30000; // refresh online/offline status every 30s — same cadence as GPS Devices, doesn't need to be as frequent as position polling

// Distinct colors cycled per client so all of their trucks are visually grouped on the map
const CLIENT_COLOR_PALETTE = ['#3b82f6', '#f97316', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#f59e0b', '#ef4444', '#14b8a6', '#a855f7'];
const UNASSIGNED_COLOR = '#6b7280';

// Picks the truck's genuinely CURRENT shipment for "View Trip" — an
// in-transit one takes priority (that's the actual active trip), and if
// there are several (shouldn't normally happen, but data can be messy),
// the most recently created one wins, not just whichever happens to come
// first in the array. Falls back to the most recent delivered shipment
// only if nothing is currently in transit, so there's still something
// sensible to show rather than nothing at all.
function getActiveShipment(documents, truckId) {
  const forTruck = (documents || []).filter(d => d.truckId === truckId);
  const byRecency = (a, b) => new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0);

  const inTransit = forTruck.filter(d => d.status === 'in-transit').sort(byRecency);
  if (inTransit.length > 0) return inTransit[0];

  const delivered = forTruck.filter(d => d.status === 'delivered').sort(byRecency);
  return delivered[0] || null;
}


// Fallback simulated movement for trucks with no GPS / no Traccar
// GPS names are typed in by users, so escape them before putting them in
// marker HTML.
const escapeHtml = (str) => String(str).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function simulateMovement(location, status) {
  if (status !== 'moving') return location;
  return {
    ...location,
    lat: location.lat + (Math.random() - 0.5) * 0.001,
    lng: location.lng + (Math.random() - 0.5) * 0.001,
    speed: Math.floor(25 + Math.random() * 35),
  };
}

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Smoothly moves a Leaflet marker along a multi-point path (a real road
// route, not a straight line) over `durationMs`, frame by frame — this is
// what makes the truck icon appear to travel along the actual street
// between two GPS updates, the same way a delivery-tracking app's icon
// glides along real roads rather than jumping in a straight line that
// might cut across blocks or water.
//
// Moves at constant speed along the path's actual distance (not just
// equal time per point), so it looks like real, even travel regardless of
// how the road-matched points happen to be spaced out.
//
// Returns a cancel() function — call it if a newer position arrives
// before this animation finishes, so the two don't fight each other.
function animateMarkerAlongPath(marker, path, durationMs) {
  if (!path || path.length < 2) {
    if (path?.length === 1) marker.setLatLng(path[0]);
    return () => {};
  }

  const cumDist = [0];
  for (let i = 1; i < path.length; i++) {
    cumDist.push(cumDist[i - 1] + haversineMeters(path[i - 1][0], path[i - 1][1], path[i][0], path[i][1]));
  }
  const total = cumDist[cumDist.length - 1] || 1;

  let cancelled = false;
  let rafId = null;
  const start = performance.now();

  function frame(now) {
    if (cancelled) return;
    const t = Math.min(1, (now - start) / durationMs);
    const targetDist = t * total;

    let idx = 0;
    while (idx < cumDist.length - 2 && cumDist[idx + 1] < targetDist) idx++;
    const segStart = cumDist[idx];
    const segEnd = cumDist[idx + 1] ?? segStart;
    const segT = segEnd > segStart ? (targetDist - segStart) / (segEnd - segStart) : 0;
    const p0 = path[idx];
    const p1 = path[idx + 1] ?? p0;

    marker.setLatLng([p0[0] + (p1[0] - p0[0]) * segT, p0[1] + (p1[1] - p0[1]) * segT]);

    if (t < 1) rafId = requestAnimationFrame(frame);
  }
  rafId = requestAnimationFrame(frame);

  return () => { cancelled = true; if (rafId) cancelAnimationFrame(rafId); };
}

export default function LiveTracking({ clientFilter }) {
  const { trucks, gpsDevices, clients, documents, currentUser, recordStatusChange, checkGeofenceViolations, geofences, focusTruckRequest, clearFocusTruckRequest, travelHistory } = useApp();
  const { isMobile } = useResponsive();
  const [tripView, setTripView] = useState(null); // { truck, shipment }
  const [expandedClients, setExpandedClients] = useState(() => new Set());
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef({});
  const truckAnimCancelRef = useRef({}); // truckId -> cancel() for its in-flight road-following animation
  const geofenceLayersRef = useRef({});
  const trailLayersRef = useRef([]); // road-matched trail line(s) for the currently selected truck
  const [trailLoading, setTrailLoading] = useState(false);
  const pollRef = useRef(null);
  const prevStatusesRef = useRef({}); // truckId -> last known status, for transition detection

  const [selectedTruck, setSelectedTruck] = useState(null);
  const [truckLocations, setTruckLocations] = useState({});
  const [truckStatuses, setTruckStatuses] = useState({});
  const [mapLoaded, setMapLoaded] = useState(false);
  const [traccarOnline, setTraccarOnline] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [connectionError, setConnectionError] = useState('');

  const displayTrucks = useMemo(() => (
    clientFilter
      ? trucks.filter(t => t.clientId === clientFilter)
      : currentUser?.role === 'client'
      ? trucks.filter(t => t.clientId === currentUser.clientId)
      : trucks
  ), [trucks, clientFilter, currentUser]);

  // Group trucks by client for admin/dispatcher — lets them drill down per
  // client instead of scrolling one long flat list. Not used for the
  // 'client' role, since they only ever see their own trucks anyway.
  const showClientGroups = !clientFilter && currentUser?.role !== 'client';
  const clientGroups = useMemo(() => {
    if (!showClientGroups) return null;
    const groups = {};
    displayTrucks.forEach(truck => {
      const key = truck.clientId || '__unassigned__';
      if (!groups[key]) groups[key] = [];
      groups[key].push(truck);
    });
    const entries = Object.entries(groups).map(([clientId, list]) => {
      const client = clientId === '__unassigned__' ? null : clients.find(c => c.id === clientId);
      return { clientId, name: client ? client.name : 'Unassigned', trucks: list };
    });
    // Named clients alphabetically first, "Unassigned" bucket last
    entries.sort((a, b) => {
      if (a.clientId === '__unassigned__') return 1;
      if (b.clientId === '__unassigned__') return -1;
      return a.name.localeCompare(b.name);
    });
    return entries;
  }, [showClientGroups, displayTrucks, clients]);

  const toggleClientGroup = (clientId) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  // Assign each client a stable, distinct color so their trucks are visually
  // grouped together on the map (cycling through the palette in the same
  // order the sidebar lists them).
  const clientColorMap = useMemo(() => {
    if (!clientGroups) return {};
    const map = {};
    let i = 0;
    clientGroups.forEach(group => {
      map[group.clientId] = group.clientId === '__unassigned__'
        ? UNASSIGNED_COLOR
        : CLIENT_COLOR_PALETTE[i++ % CLIENT_COLOR_PALETTE.length];
    });
    return map;
  }, [clientGroups]);

  const getTruckMapColor = useCallback((truck) => {
    if (showClientGroups) {
      const key = truck.clientId || '__unassigned__';
      return clientColorMap[key] || truck.color || '#3b82f6';
    }
    return truck.color || '#3b82f6';
  }, [showClientGroups, clientColorMap]);

  // What actually gets drawn on the map: when grouped (admin/dispatcher) and
  // at least one client is expanded, show ONLY that client's trucks. With
  // nothing expanded (or for the client role, where grouping doesn't apply),
  // show everything as before.
  const mapTrucks = useMemo(() => {
    if (showClientGroups && expandedClients.size > 0) {
      return displayTrucks.filter(t => expandedClients.has(t.clientId || '__unassigned__'));
    }
    return displayTrucks;
  }, [showClientGroups, expandedClients, displayTrucks]);

  // ── Initialize fallback locations ────────────────────────────────────────────
  useEffect(() => {
    const locs = {};
    displayTrucks.forEach(t => { locs[t.id] = { ...t.location }; });
    setTruckLocations(locs);
    const stats = {};
    displayTrucks.forEach(t => { stats[t.id] = t.status; });
    setTruckStatuses(stats);
    prevStatusesRef.current = { ...stats };
  }, []); // eslint-disable-line

  // ── Traccar position polling ─────────────────────────────────────────────────
  const pollingInFlight = useRef(false);
  const [connectivity, setConnectivity] = useState({}); // traccarDeviceId -> Traccar's own device object ({ status: 'online'|'offline'|'unknown', lastUpdate }), same source of truth as GPS Devices

  const pollPositions = useCallback(async () => {
    if (!TraccarConfig.isConfigured()) return;

    // Guard against overlapping cycles: if a previous poll is still
    // working through reverse-geocoding (e.g. slowed down by retries),
    // skip this tick entirely rather than starting a second cycle on top
    // of it — overlapping cycles were the actual cause of the same
    // coordinates getting reverse-geocoded many times in a row, since
    // each cycle started from a stale "previous position" and thought
    // the truck had moved again.
    if (pollingInFlight.current) return;
    pollingInFlight.current = true;

    // Everything below is wrapped in try/finally as of this fix — an
    // earlier version only wrapped the network call, which meant an
    // unexpected error anywhere else in this function (even something as
    // simple as a momentarily-stale displayTrucks/gpsDevices reference
    // during a re-render) could leave the flag above stuck at `true`
    // forever. Every future poll tick would then silently bail out at the
    // guard check above with no console error at all — GPS position
    // updates and history recording would just stop completely, with
    // nothing to explain why. try/finally here guarantees the flag always
    // gets released no matter what happens inside.
    try {
      // Collect Traccar device IDs for trucks with GPS assigned
      const traccarIds = [];
      const traccarIdToTruck = {};
      displayTrucks.forEach(truck => {
        const gps = gpsDevices.find(g => g.id === truck.gpsDeviceId);
        if (gps?.traccarDeviceId) {
          traccarIds.push(gps.traccarDeviceId);
          traccarIdToTruck[gps.traccarDeviceId] = truck;
        }
      });

      if (traccarIds.length === 0) return; // No linked Traccar devices

      const positions = await fetchPositions(traccarIds);
      setTraccarOnline(true);
      setConnectionError('');
      setLastUpdate(new Date());

      const newLocs = { ...truckLocations };
      const newStats = { ...truckStatuses };

      for (const pos of positions) {
        const truck = traccarIdToTruck[pos.deviceId];
        if (!truck) continue;

        const parsed = parsePosition(pos);

        // Never trust an invalid fix as this truck's live position — this is
        // what was letting some units show up clear across the world (often
        // China) instead of their real location in the Philippines. Keep
        // whatever the last known GOOD position was (already sitting in
        // newLocs, seeded from current state above) rather than overwriting it
        // with a stale/garbage coordinate. (No on-screen warning for this —
        // the truck simply stays where it last was reliably seen.)
        if (!parsed.valid) continue;

        // Reverse geocode (throttled — only if position changed significantly)
        const prev = newLocs[truck.id];
        const moved = !prev || Math.abs(parsed.lat - prev.lat) > 0.0001 || Math.abs(parsed.lng - prev.lng) > 0.0001;
        if (moved) {
          const address = await reverseGeocode(parsed.lat, parsed.lng, { skipNearbySearch: true });
          parsed.address = address;
        } else {
          parsed.address = prev?.address || '';
        }

        newLocs[truck.id] = parsed;

        // Update truck status based on real speed, unless the device is
        // known offline — that takes priority over whatever speed this
        // (possibly stale) position happens to report.
        const newStatus = getStatusFromPosition(pos, connectivity[pos.deviceId]);
        newStats[truck.id] = newStatus;

        // Detect transition → record travel history + trigger idle alert
        const prevStatus = prevStatusesRef.current[truck.id];
        if (prevStatus !== newStatus && (newStatus === 'idle' || newStatus === 'moving')) {
          recordStatusChange(truck, newStatus, parsed);
        }
        prevStatusesRef.current[truck.id] = newStatus;

        // Geofence check runs on every position update, not just status
        // transitions — a truck can cross a zone boundary mid-movement.
        checkGeofenceViolations(truck, parsed);
      }

      // The loop above only corrects a truck's status when Traccar
      // actually sends a fresh position for it THIS cycle. But once a
      // device has been offline long enough, Traccar's positions
      // endpoint commonly stops returning anything for it at all — so
      // that truck never enters the loop again, and its status just
      // freezes forever at whatever it last was (e.g. stuck showing
      // "Moving" even though the device is now flagged offline). Catch
      // that here: for every truck with a linked device that connectivity
      // doesn't positively confirm as online, force its status to
      // 'offline' regardless of whether we got a position for it this
      // cycle — this is a pure display correction, not a real
      // transition, so it doesn't touch location data, geofences, or
      // travel history.
      //
      // Deliberately treats "no connectivity record found at all" the
      // same as "confirmed offline" (not just device.status !== 'online'
      // on a record that exists) — a device whose STORED traccarDeviceId
      // no longer matches anything in the current Traccar device list
      // (see the "outdated link" issue/fix in Settings → GPS Server
      // Devices) would otherwise never be found in `connectivity` at
      // all, silently skipping this correction and leaving a stale
      // "Moving" status frozen forever despite the Device Offline badge
      // (which already defaults to "offline" for this exact same missing-
      // data case) correctly showing something is wrong.
      displayTrucks.forEach(truck => {
        const gps = gpsDevices.find(g => g.id === truck.gpsDeviceId);
        if (!gps?.traccarDeviceId) return;
        if (Object.keys(connectivity).length === 0) return; // connectivity hasn't loaded yet at all — don't guess before we actually know
        const device = connectivity[gps.traccarDeviceId];
        const confirmedOnline = device?.status === 'online';
        console.log(`[SDT-offline-check] ${truck.plateNumber}: traccarDeviceId=${gps.traccarDeviceId}, connectivity record found=${!!device}, device.status=${device?.status}, confirmedOnline=${confirmedOnline}, current newStats=${newStats[truck.id]}`);
        if (!confirmedOnline && newStats[truck.id] !== 'offline') {
          console.log(`[SDT-offline-check] ${truck.plateNumber}: CORRECTING to offline (was ${newStats[truck.id]})`);
          newStats[truck.id] = 'offline';
          prevStatusesRef.current[truck.id] = 'offline'; // so a genuine idle/moving reading once it reconnects is correctly seen as a new transition, not a no-op
        }
      });

      setTruckLocations(newLocs);
      setTruckStatuses(newStats);
    } catch (e) {
      console.error('GPS poll error:', e);
      setTraccarOnline(false);
      setConnectionError(e.message);
    } finally {
      pollingInFlight.current = false;
    }
  }, [displayTrucks, gpsDevices, truckLocations, truckStatuses, recordStatusChange, checkGeofenceViolations, connectivity]);

  // Start polling when Traccar is configured — bootstraps from the shared
  // backend config first (works for every user/role, including clients,
  // who have no Settings access to configure this themselves), falling
  // back to whatever's cached locally if that call fails for any reason.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadCredentialsFromBackend();
      if (cancelled) return;
      if (TraccarConfig.isConfigured()) {
        setTraccarOnline(true);
        pollPositions();
        pollRef.current = setInterval(pollPositions, POLL_INTERVAL);
      }
    })();
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line

  // Poll each device's real online/offline status from the GPS server —
  // the SAME source of truth GPS Devices uses, so the two pages can never
  // disagree. This is what was missing before: the Idle/Moving badge here
  // was computed purely from the SPEED of a truck's last reported
  // position, with no awareness of whether the device was still actually
  // communicating at all — a device that went offline hours ago, whose
  // last position happened to have speed 0, would show "Idle" forever
  // instead of "Offline", even though GPS Devices correctly showed it as
  // offline the whole time.
  useEffect(() => {
    if (!traccarOnline) return;
    let cancelled = false;
    async function refresh() {
      try {
        const devices = await fetchTraccarDevices();
        if (cancelled) return;
        const map = {};
        devices.forEach(d => { map[d.id] = d; });
        setConnectivity(map);
      } catch {
        // Silent — connectivity indicators just won't update this cycle
      }
    }
    refresh();
    const interval = setInterval(refresh, CONNECTIVITY_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [traccarOnline]);

  // Re-start polling if config changes (e.g. user just connected Traccar)
  useEffect(() => {
    if (traccarOnline) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollPositions();
      pollRef.current = setInterval(pollPositions, POLL_INTERVAL);
    }
  }, [traccarOnline]); // eslint-disable-line

  // ── Fallback simulation for trucks WITHOUT real GPS ───────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      setTruckLocations(prev => {
        const next = { ...prev };
        const moved = [];
        displayTrucks.forEach(t => {
          const gps = gpsDevices.find(g => g.id === t.gpsDeviceId);
          const hasRealGPS = gps?.traccarDeviceId && traccarOnline;
          if (!hasRealGPS && prev[t.id]) {
            const currentStatus = truckStatuses[t.id] || t.status;
            const newLoc = simulateMovement(prev[t.id], currentStatus);
            next[t.id] = newLoc;
            moved.push({ truck: t, loc: newLoc });
          }
        });
        // Geofence check for each truck's freshly simulated position
        moved.forEach(({ truck, loc }) => checkGeofenceViolations(truck, loc));
        return next;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [displayTrucks.length, traccarOnline]); // eslint-disable-line

  // ── Simulated idle/moving status transitions for demo trucks (no real GPS) ──
  useEffect(() => {
    const interval = setInterval(() => {
      setTruckStatuses(prev => {
        const next = { ...prev };
        let changed = false;

        displayTrucks.forEach(t => {
          const gps = gpsDevices.find(g => g.id === t.gpsDeviceId);
          const hasRealGPS = gps?.traccarDeviceId && traccarOnline;
          if (hasRealGPS) return; // real GPS handles its own transitions
          if (t.status === 'maintenance' || t.status === 'offline') return;

          const currentStatus = prev[t.id] || t.status;
          // Random chance to toggle between moving and idle, simulating real-world stops
          const shouldToggle = Math.random() < 0.18; // ~18% chance per tick
          if (shouldToggle) {
            const newStatus = currentStatus === 'moving' ? 'idle' : 'moving';
            next[t.id] = newStatus;
            changed = true;

            const loc = truckLocations[t.id] || t.location;
            const prevStatus = prevStatusesRef.current[t.id];
            if (prevStatus !== newStatus) {
              recordStatusChange(t, newStatus, loc || {});
            }
            prevStatusesRef.current[t.id] = newStatus;
          }
        });

        return changed ? next : prev;
      });
    }, 12000); // check every 12 seconds
    return () => clearInterval(interval);
  }, [displayTrucks, gpsDevices, traccarOnline, truckLocations, recordStatusChange]);

  // ── Leaflet map init ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;

    function initMap(L) {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        markersRef.current = {};
        Object.values(truckAnimCancelRef.current).forEach(cancel => cancel());
        truckAnimCancelRef.current = {};
        geofenceLayersRef.current = {};
        trailLayersRef.current = [];
      }
      const map = L.map(mapRef.current, { center: [14.5995, 120.9842], zoom: 12, zoomControl: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '©OpenStreetMap contributors', maxZoom: 19,
      }).addTo(map);
      leafletMapRef.current = map;
      setMapLoaded(true);
    }

    const L = window.L;
    if (!L) {
      const existing = document.querySelector('script[src*="leaflet.js"]');
      if (!existing) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.crossOrigin = 'anonymous';
        script.onload = () => initMap(window.L);
        document.head.appendChild(script);
      } else {
        existing.addEventListener('load', () => initMap(window.L));
      }
    } else {
      initMap(L);
    }

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        markersRef.current = {};
        Object.values(truckAnimCancelRef.current).forEach(cancel => cancel());
        truckAnimCancelRef.current = {};
        geofenceLayersRef.current = {};
        trailLayersRef.current = [];
        setMapLoaded(false);
      }
    };
  }, []);

  // Moves a truck's marker to its new position by animating along the
  // actual road route between where it currently is and where it just
  // reported being — not a straight pixel interpolation. Falls back to an
  // instant snap for negligible movement (avoids a pointless route fetch
  // for a truck that's essentially stationary), if the route fetch fails
  // for any reason, or if the browser tab isn't currently visible —
  // requestAnimationFrame is paused by the browser on background tabs, so
  // an animation started (or still in flight) while the tab isn't active
  // would otherwise freeze the marker at some in-between position instead
  // of ever reaching the truck's real, current location.
  const moveMarkerSmoothly = useCallback((truck, marker, newLat, newLng, status) => {
    if (truckAnimCancelRef.current[truck.id]) {
      truckAnimCancelRef.current[truck.id]();
      delete truckAnimCancelRef.current[truck.id];
    }

    if (document.hidden) {
      marker.setLatLng([newLat, newLng]); // tab isn't visible — animating would just freeze mid-flight, so skip straight to correct
      return;
    }

    const current = marker.getLatLng();
    const distance = haversineMeters(current.lat, current.lng, newLat, newLng);

    // Skip tiny position changes as GPS jitter — but ONLY while the truck
    // is reported idle, where any wobble really is just noise. A truck
    // already confirmed "moving" can easily cover as little as ~8m per
    // poll at just-above-the-moving-threshold speeds (slow maneuvering,
    // creeping in traffic, backing into a dock) — filtering that out too
    // was exactly what caused the icon to sit frozen while the badge
    // still correctly said "Moving".
    if (status === 'idle' && distance < 8) {
      marker.setLatLng([newLat, newLng]); // negligible movement — just snap, nothing to animate
      return;
    }
    if (distance < 1) {
      return; // truly no change at all — nothing to do, even for a moving truck
    }

    const animateDuration = Math.max(2000, POLL_INTERVAL - 500); // leave a little buffer before the next update lands
    let cancelled = false;

    getRoute({ lat: current.lat, lng: current.lng }, { lat: newLat, lng: newLng })
      .then(route => {
        if (cancelled || document.hidden) { marker.setLatLng([newLat, newLng]); return; }
        const path = route?.geometry?.length > 1 ? route.geometry : [[current.lat, current.lng], [newLat, newLng]];
        truckAnimCancelRef.current[truck.id] = animateMarkerAlongPath(marker, path, animateDuration);
      })
      .catch(() => {
        if (cancelled) return;
        if (document.hidden) { marker.setLatLng([newLat, newLng]); return; }
        truckAnimCancelRef.current[truck.id] = animateMarkerAlongPath(marker, [[current.lat, current.lng], [newLat, newLng]], animateDuration);
      });

    truckAnimCancelRef.current[truck.id] = () => { cancelled = true; };
  }, []);

  // Safety net on top of the check inside moveMarkerSmoothly: the moment
  // this tab becomes visible again after being backgrounded, force every
  // truck marker straight to its latest known-correct position and cancel
  // any animation that might still technically be "in flight" from before
  // the tab was hidden. This guarantees a truck can never stay visually
  // "lost" on the map for longer than it takes to switch back to this
  // tab, regardless of whatever sequence of events happened while it
  // wasn't being watched.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) return;
      Object.entries(markersRef.current).forEach(([truckId, marker]) => {
        if (truckAnimCancelRef.current[truckId]) {
          truckAnimCancelRef.current[truckId]();
          delete truckAnimCancelRef.current[truckId];
        }
        const loc = truckLocations[truckId];
        if (loc?.lat) marker.setLatLng([loc.lat, loc.lng]);
      });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [truckLocations]);

  // ── Update map markers ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapLoaded || !leafletMapRef.current) return;
    const L = window.L;
    const map = leafletMapRef.current;

    mapTrucks.forEach(truck => {
      const loc = truckLocations[truck.id];
      if (!loc || !loc.lat || !loc.lng) return;

      const liveStatus = truckStatuses[truck.id] || truck.status;
      const isSelected = selectedTruck?.id === truck.id;
      const gps = gpsDevices.find(g => g.id === truck.gpsDeviceId);
      const hasRealGPS = !!(gps?.traccarDeviceId && traccarOnline);
      const markerColor = getTruckMapColor(truck);

      const size = isSelected ? 46 : 38;
      const svgIcon = `
        <svg width="${size}" height="${size}" viewBox="0 0 46 46" xmlns="http://www.w3.org/2000/svg">
          ${liveStatus === 'moving' ? `
            <circle cx="23" cy="23" r="21" fill="none" stroke="${markerColor}" stroke-width="2" opacity="0.25">
              <animate attributeName="r" values="21;28;21" dur="2s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite"/>
            </circle>` : ''}
          <circle cx="23" cy="23" r="${isSelected ? 20 : 17}"
            fill="${markerColor}"
            fill-opacity="${isSelected ? 1 : 0.9}"
            stroke="${isSelected ? '#fff' : 'rgba(255,255,255,0.35)'}"
            stroke-width="${isSelected ? 2.5 : 1.5}"/>
          <text x="23" y="28" text-anchor="middle" font-size="15" fill="white">🚛</text>
          ${hasRealGPS ? `<circle cx="35" cy="11" r="6" fill="#10b981" stroke="#080d1a" stroke-width="1.5"/>
            <text x="35" y="15" text-anchor="middle" font-size="8" fill="white">●</text>` : ''}
        </svg>
      `;

      // Label under the icon: the GPS device's own name (e.g. "GPS-1"); a truck
      // with no GPS device linked has none, so fall back to its plate number so
      // every icon on the map is still identifiable.
      const labelText = escapeHtml(gps?.deviceId || truck.plateNumber || '');
      const labelHtml = labelText ? `
        <div style="position:absolute;top:${size + 1}px;left:50%;transform:translateX(-50%);
          max-width:130px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
          padding:2px 7px;border-radius:6px;font-family:'DM Sans',sans-serif;font-size:10px;line-height:1.3;
          font-weight:${isSelected ? 800 : 700};color:#fff;background:rgba(8,13,26,${isSelected ? 0.95 : 0.82});
          border:1px solid ${markerColor};box-shadow:0 1px 4px rgba(0,0,0,0.45);">${labelText}</div>` : '';

      const icon = L.divIcon({
        html: `<div style="position:relative;width:${size}px;height:${size}px;">${svgIcon}${labelHtml}</div>`,
        className: '',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });

      if (markersRef.current[truck.id]) {
        moveMarkerSmoothly(truck, markersRef.current[truck.id], loc.lat, loc.lng, liveStatus);
        markersRef.current[truck.id].setIcon(icon);
      } else {
        const marker = L.marker([loc.lat, loc.lng], { icon })
          .addTo(map)
          .on('click', () => setSelectedTruck(truck));
        markersRef.current[truck.id] = marker;
      }
    });

    // Remove markers for trucks no longer in the visible set (either gone
    // entirely, or filtered out because their client group is collapsed)
    Object.keys(markersRef.current).forEach(id => {
      if (!mapTrucks.find(t => t.id === id)) {
        leafletMapRef.current?.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
        if (truckAnimCancelRef.current[id]) {
          truckAnimCancelRef.current[id]();
          delete truckAnimCancelRef.current[id];
        }
      }
    });

    // Draw a circle or custom-shape polygon for every active geofence
    // belonging to a currently visible truck, so the zone boundary is
    // visible right on the live map.
    const visibleGeofences = geofences.filter(g => g.active && mapTrucks.find(t => t.id === g.truckId) && (
      g.shape === 'polygon' ? g.points?.length >= 3 : g.centerLat != null
    ));
    visibleGeofences.forEach(gf => {
      const isPolygon = gf.shape === 'polygon';
      const existing = geofenceLayersRef.current[gf.id];

      if (existing && existing._isPolygon === isPolygon) {
        // Same shape type as before — update in place
        if (isPolygon) {
          existing.setLatLngs(gf.points.map(p => [p.lat, p.lng]));
        } else {
          existing.setLatLng([gf.centerLat, gf.centerLng]);
          existing.setRadius(gf.radiusMeters);
        }
      } else {
        // First draw, or shape type changed since last render — recreate
        if (existing) map.removeLayer(existing);
        const style = { color: gf.color || '#3b82f6', weight: 2, fillColor: gf.color || '#3b82f6', fillOpacity: 0.08, dashArray: '6,6' };
        const layer = isPolygon
          ? L.polygon(gf.points.map(p => [p.lat, p.lng]), style)
          : L.circle([gf.centerLat, gf.centerLng], { ...style, radius: gf.radiusMeters });
        layer.addTo(map).bindPopup(`<div style="font-family:DM Sans,sans-serif;font-size:12px;"><strong>${gf.name}</strong></div>`);
        layer._isPolygon = isPolygon;
        geofenceLayersRef.current[gf.id] = layer;
      }
    });
    Object.keys(geofenceLayersRef.current).forEach(id => {
      if (!visibleGeofences.find(g => g.id === id)) {
        leafletMapRef.current?.removeLayer(geofenceLayersRef.current[id]);
        delete geofenceLayersRef.current[id];
      }
    });
  }, [truckLocations, mapLoaded, selectedTruck, truckStatuses, traccarOnline, mapTrucks, gpsDevices, getTruckMapColor, geofences, moveMarkerSmoothly]);

  // Pan to selected truck
  useEffect(() => {
    if (!selectedTruck || !leafletMapRef.current) return;
    const loc = truckLocations[selectedTruck.id];
    if (loc?.lat) leafletMapRef.current.panTo([loc.lat, loc.lng], { animate: true });
  }, [selectedTruck, truckLocations]);

  // Draw a road-snapped trail showing where the selected truck has been
  // recently, leading up to its current live position — same map-matching
  // approach as Travel History, so it traces actual roads instead of
  // connecting raw GPS points with straight lines. Only for the selected
  // truck (not everyone at once) to avoid clutter and excess API calls.
  useEffect(() => {
    const L = window.L;
    const map = leafletMapRef.current;
    if (!L || !map) return;

    // Always clear the previous trail first — covers both "no truck
    // selected" and "switched to a different truck" cases.
    trailLayersRef.current.forEach(l => map.removeLayer(l));
    trailLayersRef.current = [];
    setTrailLoading(false);

    if (!selectedTruck) return;

    const recentHistory = (travelHistory || [])
      .filter(h => h.truckId === selectedTruck.id && h.lat != null && h.lng != null)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .slice(-60); // last ~60 recorded points is plenty for a recent trail without overloading the matcher

    // Include the truck's current live position as the trail's end point,
    // so the line reaches exactly where the marker is right now.
    const liveLoc = truckLocations[selectedTruck.id];
    const points = [...recentHistory];
    if (liveLoc?.lat) points.push({ lat: liveLoc.lat, lng: liveLoc.lng, timestamp: new Date().toISOString() });

    if (points.length < 2) return;

    // Draw an immediate, clearly visible fallback line right away so
    // there's always something on screen — then swap it for the properly
    // road-matched version if that succeeds. Never leave the trail blank.
    const fallbackLine = L.polyline(points.map(p => [p.lat, p.lng]), { color: '#93c5fd', weight: 3, opacity: 0.5, dashArray: '2,6', lineCap: 'round', lineJoin: 'round' }).addTo(map);
    trailLayersRef.current.push(fallbackLine);

    let cancelled = false;
    setTrailLoading(true);
    matchToRoad(points).then(segments => {
      if (cancelled || !leafletMapRef.current) return;
      setTrailLoading(false);
      if (!segments) return; // keep the fallback line as-is

      leafletMapRef.current.removeLayer(fallbackLine);
      trailLayersRef.current = trailLayersRef.current.filter(l => l !== fallbackLine);
      // Same turn-by-turn nav look as Travel History: soft white casing
      // under a solid, vibrant blue line, rounded joins throughout.
      segments.forEach(seg => {
        const casing = L.polyline(seg, { color: '#ffffff', weight: 8, opacity: 0.85, lineCap: 'round', lineJoin: 'round' }).addTo(leafletMapRef.current);
        const main = L.polyline(seg, { color: '#2563eb', weight: 4, opacity: 1, lineCap: 'round', lineJoin: 'round' }).addTo(leafletMapRef.current);
        trailLayersRef.current.push(casing, main);
      });
    });

    return () => { cancelled = true; };
  }, [selectedTruck, travelHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Consume a "View Truck" request from a notification (idle or geofence
  // alert): select that truck, expand its client group if the sidebar is
  // grouped, and zoom the map in on it — then clear the request so it
  // doesn't re-trigger.
  useEffect(() => {
    if (!focusTruckRequest?.truckId || !mapLoaded) return;
    const truck = trucks.find(t => t.id === focusTruckRequest.truckId);
    if (!truck) { clearFocusTruckRequest(); return; }

    setSelectedTruck(truck);

    if (showClientGroups) {
      const key = truck.clientId || '__unassigned__';
      setExpandedClients(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
    }

    const loc = truckLocations[truck.id] || truck.location;
    if (loc?.lat && leafletMapRef.current) {
      leafletMapRef.current.setView([loc.lat, loc.lng], 15, { animate: true });
    }

    clearFocusTruckRequest();
  }, [focusTruckRequest, mapLoaded, trucks, truckLocations, showClientGroups, clearFocusTruckRequest]);

  // Fit the map to frame exactly the trucks currently visible whenever the
  // expanded client filter changes (but not on every routine location poll —
  // only when the user actually toggles a client group open/closed).
  const prevGroupKeyRef = useRef('');
  useEffect(() => {
    if (!mapLoaded || !leafletMapRef.current) return;
    const key = Array.from(expandedClients).sort().join(',');
    if (key === prevGroupKeyRef.current) return;
    prevGroupKeyRef.current = key;

    const points = mapTrucks
      .map(t => truckLocations[t.id])
      .filter(loc => loc?.lat)
      .map(loc => [loc.lat, loc.lng]);

    if (points.length === 1) {
      leafletMapRef.current.setView(points[0], 14, { animate: true });
    } else if (points.length > 1) {
      leafletMapRef.current.fitBounds(points, { padding: [40, 40], animate: true });
    }
  }, [expandedClients, mapTrucks, truckLocations, mapLoaded]);

  const formatTime = (date) => {
    if (!date) return '—';
    return date.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? 12 : 16,
      height: isMobile ? 'auto' : 'calc(100vh - 120px)',
      minHeight: isMobile ? 'auto' : 500,
    }}>
      {/* Map — shown first on mobile so it's the primary focus */}
      <div style={{
        order: isMobile ? 1 : 2,
        flex: isMobile ? 'none' : 1,
        height: isMobile ? '50vh' : 'auto',
        minHeight: isMobile ? 320 : 'auto',
        position: 'relative', borderRadius: 16, overflow: 'hidden', border: '1px solid var(--border)',
      }}>
        <div ref={mapRef} className="sdt-live-map" style={{ width: '100%', height: '100%' }} />

        {trailLoading && (
          <div style={{ position: 'absolute', bottom: 16, left: 16, zIndex: 1000, background: 'var(--bgModal)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px', fontSize: 11, color: 'var(--textMuted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, border: '2px solid rgba(59,130,246,0.3)', borderTopColor: '#3b82f6', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
            Tracing recent path...
          </div>
        )}

        {/* Selected truck info panel */}
        {selectedTruck && (() => {
          const loc = truckLocations[selectedTruck.id] || selectedTruck.location;
          const liveStatus = truckStatuses[selectedTruck.id] || selectedTruck.status;
          const status = TRUCK_STATUSES[liveStatus] || TRUCK_STATUSES.offline;
          const client = clients?.find(c => c.id === selectedTruck.clientId);
          const gps = gpsDevices.find(g => g.id === selectedTruck.gpsDeviceId);
          const hasRealGPS = !!(gps?.traccarDeviceId && traccarOnline);
          const deviceOnline = gps?.traccarDeviceId ? connectivity[gps.traccarDeviceId]?.status === 'online' : null;
          const activeShipment = currentUser?.role === 'client'
            ? getActiveShipment(documents, selectedTruck.id)
            : null;
          return (
            <div style={{ position: 'absolute', bottom: isMobile ? 10 : 20, left: isMobile ? 10 : 20, right: isMobile ? 10 : 20, background: 'var(--bgModal)', border: '1px solid var(--borderInput)', borderRadius: 14, padding: isMobile ? '12px 14px' : '16px 20px', backdropFilter: 'blur(16px)', zIndex: 1000, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', maxHeight: isMobile ? '85%' : 'none', overflowY: isMobile ? 'auto' : 'visible' }}>
              <div style={{ display: 'flex', gap: isMobile ? 10 : 16, alignItems: isMobile ? 'flex-start' : 'center', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: isMobile ? '1 1 100%' : 'none' }}>
                  <div style={{ width: 10, height: 44, borderRadius: 3, background: selectedTruck.color, flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? 15 : 18, color: 'var(--textPrimary)' }}>{selectedTruck.plateNumber}</div>
                    <div style={{ fontSize: 12, color: 'var(--textMuted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedTruck.model} · {selectedTruck.driver}</div>
                    <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Badge color={status.color} bg={status.bg}>{status.label}</Badge>
                      <Badge color={hasRealGPS ? '#10b981' : '#6b7280'}>{hasRealGPS ? '● Live GPS' : '○ Simulated'}</Badge>
                      {hasRealGPS && (
                        <Badge color={deviceOnline ? '#10b981' : '#6b7280'}>{deviceOnline ? '📶 Device Online' : '📵 Device Offline'}</Badge>
                      )}
                    </div>
                  </div>
                  {!isMobile && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto', flexShrink: 0 }}>
                      {activeShipment && (
                        <button
                          onClick={() => setTripView({ truck: selectedTruck, shipment: activeShipment })}
                          style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: 6, color: '#60a5fa', fontSize: 11, fontWeight: 700, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          🗺️ View Trip
                        </button>
                      )}
                      <button onClick={() => setSelectedTruck(null)} style={{ background: 'none', border: '1px solid var(--borderInput)', borderRadius: 6, color: 'var(--textMuted)', cursor: 'pointer', padding: '4px 8px', fontSize: 11, flexShrink: 0 }}>✕</button>
                    </div>
                  )}
                </div>
                <div style={{ flex: isMobile ? '1 1 100%' : 1, display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12 }}>
                  {[
                    { label: 'Speed', value: `${loc?.speed || 0} km/h` },
                    { label: 'Heading', value: loc?.heading || '—' },
                    { label: 'Location', value: loc?.address || '—' },
                    { label: 'Client', value: client?.name || 'Unassigned' },
                  ].map(item => (
                    <div key={item.label}>
                      <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>{item.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--textSecondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</div>
                    </div>
                  ))}
                </div>
                {isMobile && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    {activeShipment && (
                      <button
                        onClick={() => setTripView({ truck: selectedTruck, shipment: activeShipment })}
                        style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: 6, color: '#60a5fa', fontSize: 10, fontWeight: 700, padding: '4px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        🗺️ Trip
                      </button>
                    )}
                    <button onClick={() => setSelectedTruck(null)} style={{ background: 'none', border: '1px solid var(--borderInput)', borderRadius: 6, color: 'var(--textMuted)', cursor: 'pointer', padding: '4px 8px', fontSize: 11, flexShrink: 0 }}>✕</button>
                  </div>
                )}
              </div>
              {loc?.fixTime && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', fontSize: 10, color: 'var(--textFaint)' }}>
                  GPS Fix: {new Date(loc.fixTime).toLocaleString('en-PH')}
                  {loc?.altitude !== undefined && ` · Alt: ${Math.round(loc.altitude)}m`}
                  {loc?.accuracy !== undefined && ` · Accuracy: ±${Math.round(loc.accuracy)}m`}
                </div>
              )}
            </div>
          );
        })()}

        {/* Top-right legend */}
        <div style={{ position: 'absolute', top: isMobile ? 10 : 16, right: isMobile ? 10 : 16, zIndex: 1000, maxWidth: isMobile ? 'calc(100% - 20px)' : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, background: 'var(--bgCard)', border: '1px solid var(--borderInput)', borderRadius: 10, padding: isMobile ? '5px 10px' : '7px 14px', flexWrap: 'wrap' }}>
            {Object.entries(TRUCK_STATUSES).map(([k, v]) => {
              const count = mapTrucks.filter(t => (truckStatuses[t.id] || t.status) === k).length;
              if (count === 0) return null;
              return (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: isMobile ? 10 : 11 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: v.color }} />
                  <span style={{ color: v.color, fontWeight: 600 }}>{count}</span>
                  {!isMobile && <span style={{ color: 'var(--textFaint)' }}>{v.label}</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Truck list */}
      <div style={{
        order: isMobile ? 2 : 1,
        width: isMobile ? '100%' : 290,
        flexShrink: 0,
        display: 'flex', flexDirection: 'column', gap: 10,
        overflowY: isMobile ? 'visible' : 'auto',
        maxHeight: isMobile ? 'none' : '100%',
      }}>

        {/* Connection status bar */}
        <div style={{
          padding: '10px 14px', borderRadius: 10,
          background: traccarOnline ? 'rgba(16,185,129,0.08)' : 'var(--bgHover)',
          border: `1px solid ${traccarOnline ? 'rgba(16,185,129,0.25)' : 'var(--bgTag)'}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: traccarOnline ? '#10b981' : '#6b7280', boxShadow: traccarOnline ? '0 0 8px #10b981' : 'none' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: traccarOnline ? '#34d399' : 'var(--textMuted)' }}>
              {traccarOnline ? 'GPS Live' : TraccarConfig.isConfigured() ? 'GPS Server Offline' : 'Simulation Mode'}
            </div>
            {traccarOnline && lastUpdate && (
              <div style={{ fontSize: 10, color: 'var(--textFaint)' }}>Updated {formatTime(lastUpdate)}</div>
            )}
            {connectionError && (
              <div style={{ fontSize: 10, color: '#f87171', marginTop: 1 }}>{connectionError}</div>
            )}
            {!TraccarConfig.isConfigured() && (
              <div style={{ fontSize: 10, color: 'var(--textFaint)' }}>Connect GPS server in GPS Devices</div>
            )}
          </div>
          {traccarOnline && (
            <div style={{ fontSize: 10, color: 'var(--textFaint)' }}>~{POLL_INTERVAL / 1000}s</div>
          )}
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
          {showClientGroups && clientGroups
            ? `${clientGroups.length} Client${clientGroups.length !== 1 ? 's' : ''} · ${displayTrucks.length} Truck${displayTrucks.length !== 1 ? 's' : ''}`
            : `${displayTrucks.length} Truck${displayTrucks.length !== 1 ? 's' : ''}`}
        </div>

        {showClientGroups && expandedClients.size === 0 && (
          <div style={{ fontSize: 10, color: 'var(--textFaint)', marginTop: -6 }}>
            Showing all trucks on the map · click a client below to focus on just theirs
          </div>
        )}

        {!selectedTruck && (
          <div style={{ fontSize: 10, color: 'var(--textFaint)', marginTop: -6, display: 'flex', alignItems: 'center', gap: 4 }}>
            🔵 Click a truck below to see its recent path traced on the map
          </div>
        )}

        {(() => {
          const renderTruckCard = (truck) => {
            const loc = truckLocations[truck.id] || truck.location;
            const liveStatus = truckStatuses[truck.id] || truck.status;
            const status = TRUCK_STATUSES[liveStatus] || TRUCK_STATUSES.offline;
            const gps = gpsDevices.find(g => g.id === truck.gpsDeviceId);
            const hasRealGPS = !!(gps?.traccarDeviceId);
            const deviceOnline = gps?.traccarDeviceId ? connectivity[gps.traccarDeviceId]?.status === 'online' : null;
            const isSelected = selectedTruck?.id === truck.id;
            const activeShipment = currentUser?.role === 'client'
              ? getActiveShipment(documents, truck.id)
              : null;

            return (
              <div
                key={truck.id}
                onClick={() => setSelectedTruck(truck)}
                style={{
                  background: isSelected ? 'var(--bgActive)' : 'var(--bgCard)',
                  borderTop: `1px solid ${isSelected ? 'rgba(249,115,22,0.3)' : 'var(--bgTag)'}`,
                  borderRight: `1px solid ${isSelected ? 'rgba(249,115,22,0.3)' : 'var(--bgTag)'}`,
                  borderBottom: `1px solid ${isSelected ? 'rgba(249,115,22,0.3)' : 'var(--bgTag)'}`,
                  borderLeft: showClientGroups ? `3px solid ${getTruckMapColor(truck)}` : `1px solid ${isSelected ? 'rgba(249,115,22,0.3)' : 'var(--bgTag)'}`,
                  borderRadius: 12, padding: '12px 14px', cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', background: status.color, flexShrink: 0,
                    ...(liveStatus === 'moving' ? { boxShadow: `0 0 8px ${status.color}` } : {}),
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13, color: 'var(--textPrimary)' }}>{truck.plateNumber}</div>
                    <div style={{ fontSize: 10, color: 'var(--textFaint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{truck.model} · {truck.driver}</div>
                  </div>
                  <Badge color={status.color} bg={status.bg}>{status.label}</Badge>
                </div>

                <div style={{ fontSize: 11, color: 'var(--textMuted)', display: 'flex', gap: 5, marginBottom: 5 }}>
                  <span>📍</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc?.address || 'Unknown'}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {liveStatus === 'moving' && (
                    <span style={{ fontSize: 11, color: '#10b981' }}>🏃 {loc?.speed || 0} km/h · {loc?.heading || 'N'}</span>
                  )}
                  {hasRealGPS && (
                    <span style={{ fontSize: 10, color: deviceOnline ? '#10b981' : '#6b7280' }}>
                      {deviceOnline ? '📶 Online' : '📵 Offline'}
                    </span>
                  )}
                  <span style={{ marginLeft: liveStatus === 'moving' ? 0 : 'auto', fontSize: 10, color: hasRealGPS ? '#10b981' : 'var(--textFaint)' }}>
                    {hasRealGPS ? '● Live GPS' : '○ Simulated'}
                  </span>
                </div>

                {activeShipment && (
                  <button
                    onClick={e => { e.stopPropagation(); setTripView({ truck, shipment: activeShipment }); }}
                    style={{ marginTop: 8, width: '100%', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 11, fontWeight: 700, padding: '6px 8px', cursor: 'pointer' }}
                  >
                    🗺️ View Trip
                  </button>
                )}
              </div>
            );
          };

          // Admin / Dispatcher: group trucks under collapsible client headers
          if (showClientGroups && clientGroups) {
            return clientGroups.map(group => {
              const isOpen = expandedClients.has(group.clientId);
              const movingCount = group.trucks.filter(t => (truckStatuses[t.id] || t.status) === 'moving').length;
              return (
                <div key={group.clientId}>
                  <button
                    onClick={() => toggleClientGroup(group.clientId)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                      background: 'var(--bgCard)', border: '1px solid var(--bgTag)', borderRadius: 10,
                      padding: '10px 12px', cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <span style={{ fontSize: 11, color: 'var(--textMuted)', transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▶</span>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: clientColorMap[group.clientId] || UNASSIGNED_COLOR, flexShrink: 0, border: '1.5px solid rgba(255,255,255,0.25)' }} />
                    <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13, color: 'var(--textPrimary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {group.name}
                    </span>
                    {movingCount > 0 && (
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 6px #10b981', flexShrink: 0 }} />
                    )}
                    <Badge color="#6b7a9a">{group.trucks.length}</Badge>
                  </button>
                  {isOpen && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, marginLeft: 10, paddingLeft: 10, borderLeft: '2px solid var(--bgTag)' }}>
                      {group.trucks.map(renderTruckCard)}
                    </div>
                  )}
                </div>
              );
            });
          }

          // Client role (or a client-filtered view): flat list, no grouping needed
          return displayTrucks.map(renderTruckCard);
        })()}
      </div>

      {/* Trip Trail View */}
      {tripView && (
        <TripView
          truck={tripView.truck}
          shipment={tripView.shipment}
          onClose={() => setTripView(null)}
        />
      )}
    </div>
  );
}
